import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import BasicModal from '../../newStudent/Modal/BasicModal';
import { useChat } from '../../../hooks/useChat';
import { useRealtimeChat } from '../../../hooks/useRealtimeChat';
import { useRealtimeUsers } from '../../../hooks/useRealtimeUsers';
import ChatList from '../ChatList';
import ChatConversation from '../ChatConversation';
import EmptyChat from '../EmptyChat';
import BulkMessage from '../BulkMessage';
import { IChatConversation } from '../../../interfaces/chat/chat.interface';
import { HiChatAlt2 } from 'react-icons/hi';
import * as S from './styles';
import NewChatModal from '../NewChatModal';
import HeaderModal from '../../agent/HeaderModal';

interface IChatModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export default function ChatModal({ isOpen, setIsOpen }: IChatModalProps) {
  const {
    currentUser,
    users,
    setUsers,
    conversations,
    setConversations,
    selectedConversation,
    messages,
    setMessages,
    loadingMoreMessages,
    hasMoreMessages,
    loadMoreMessages,
    selectConversation,
    sendMessage,
    loadConversations,
    loadUsers,
    startConversationWithUser,
    startGroupConversation,
    canAccessChat,
  } = useChat();

  const [selectedConvId, setSelectedConvId] = useState<number | null>(null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showBulkMessage, setShowBulkMessage] = useState(false);
  const [showConversationView, setShowConversationView] = useState(false);

  useEffect(() => {
    if (isOpen && currentUser && canAccessChat) {
      loadConversations();
    }
  }, [isOpen, currentUser, canAccessChat]);

  useEffect(() => {
    if (showNewChatModal && currentUser && canAccessChat) {
      loadUsers();
    }
  }, [showNewChatModal, currentUser, canAccessChat]);

  useRealtimeUsers(setUsers, currentUser?.id || null, canAccessChat);

  useRealtimeChat(
    conversations,
    setConversations,
    selectedConvId,
    currentUser?.id || null,
    setMessages,
    canAccessChat,
  );

  const handleSelectConversation = (conversation: IChatConversation) => {
    if (!canAccessChat) return;
    selectConversation(conversation);
    setSelectedConvId(conversation.id);
    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === conversation.id ? { ...conv, unreadCount: 0 } : conv,
      ),
    );

    if (window.innerWidth <= 768) {
      setShowConversationView(true);
    }
  };

  const handleBackToList = () => {
    setShowConversationView(false);
    selectConversation(null as unknown as IChatConversation);
    setSelectedConvId(null);
  };

  const handleSendMessage = async (content: string) => {
    if (!canAccessChat) return false;
    if (selectedConversation) {
      const success = await sendMessage(content);
      return success;
    }
    return false;
  };

  const handleClose = () => {
    setSelectedConvId(null);
    setIsOpen(false);
    setShowNewChatModal(false);
    setShowBulkMessage(false);
    setShowConversationView(false);
    selectConversation(null as unknown as IChatConversation);
    setSelectedConvId(null);
    setMessages([]);
  };

  const handleNewChat = async (userId: number) => {
    if (!canAccessChat) return;
    try {
      const conversation = await startConversationWithUser(userId);
      if (conversation) {
        handleSelectConversation(conversation);
      }
    } catch (error) {
      // Erro ao criar nova conversa
    }
  };

  const handleCreateGroup = async (name: string, participantIds: number[]) => {
    if (!canAccessChat) return;
    try {
      const conversation = await startGroupConversation(name, participantIds);
      if (conversation) {
        handleSelectConversation(conversation);
      }
    } catch (error) {
      // Erro ao criar grupo
    }
  };

  const handleConversationUpdate = () => {
    loadConversations();
  };

  return (
    <>
      <BasicModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        handleOnClose={handleClose}
        isFloat
        zIndex={1000}
      >
        <S.Container>
          <HeaderModal
            setOpen={setIsOpen}
            title="Chat de Suporte"
            subtitle
            icon={<HiChatAlt2 size={24} />}
            handleClose={() => {
              handleClose && handleClose();
            }}
          />
          <S.Content>
            {showBulkMessage ? (
              <BulkMessage
                isOpen={showBulkMessage}
                onClose={() => setShowBulkMessage(false)}
                users={users.filter((user) => user.id !== currentUser?.id)}
                onSuccess={handleConversationUpdate}
              />
            ) : (
              <>
                <S.Sidebar $showOnMobile={!showConversationView}>
                  <ChatList
                    conversations={conversations}
                    onConversationSelect={handleSelectConversation}
                    selectedConversationId={selectedConvId}
                    onNewChatClick={() => setShowNewChatModal(true)}
                    isModalOpen={isOpen}
                  />
                </S.Sidebar>
                <S.MainContent $showOnMobile={showConversationView}>
                  {selectedConversation && selectedConversation.id ? (
                    <ChatConversation
                      conversation={selectedConversation}
                      messages={messages}
                      onSendMessage={handleSendMessage}
                      onConversationUpdate={handleConversationUpdate}
                      onLoadMore={loadMoreMessages}
                      hasMore={hasMoreMessages}
                      loadingMore={loadingMoreMessages}
                      onBackToList={handleBackToList}
                    />
                  ) : (
                    <EmptyChat />
                  )}
                </S.MainContent>
              </>
            )}
          </S.Content>
        </S.Container>

        <NewChatModal
          isOpen={showNewChatModal}
          onClose={() => setShowNewChatModal(false)}
          users={users.filter((user) => user.id !== currentUser?.id)}
          onCreateChat={handleNewChat}
          onCreateGroup={handleCreateGroup}
        />
      </BasicModal>
    </>
  );
}

